/**
 * The letters that ship with the module, and the three things an operator can
 * do to one.
 *
 * EDIT CHANGES THE ORIGINAL. It does not open a copy. The edit is stored as a
 * row bound to the preset's id, and from that moment this shelf - and the
 * campaign picker, which reads the same rows - shows the edited wording in the
 * shipped letter's place. Fixing a sentence once fixes it everywhere the letter
 * is offered, which is the whole reason this exists: a library where the only
 * way to correct a shipped letter is to copy it produces five near-identical
 * templates and no answer to "which one is current".
 *
 * RESTORE deletes that row. Nothing overwrites what shipped, so the original is
 * always one press away, and a deploy that improves a preset reaches everyone
 * who has not edited it.
 *
 * COPY is still here for the other case: the shipped letter is the right
 * starting point for something that is not it.
 *
 * Its own file rather than more branches inside `TemplatesList`: that file is
 * about laying out two sections and loading rows, this one is about what
 * happens when somebody edits a letter they did not write, and those fail in
 * completely different ways.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Loader2, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { IconBtn } from "../../../ui";
import {
  adoptPreset,
  createTemplate,
  deleteTemplate,
  patchTemplate,
  type TemplateRow,
} from "../../../../lib/marketingTemplates";
import { instantiate, PRESETS, type Preset, type PresetLocale } from "../../../../emailBuilder";
import { slugify } from "../lib";
import { TemplateCard } from "./TemplateCard";

export function PresetShelf({
  rows,
  presetLocale,
  canManage,
  onChanged,
  onError,
}: {
  /** Every saved template. The edited ones are found here by `presetId`. */
  rows: TemplateRow[];
  presetLocale: PresetLocale;
  canManage: boolean;
  onChanged: () => void;
  onError: (key: string) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const edits = new Map(rows.filter((r) => r.presetId).map((r) => [r.presetId as string, r]));

  /** What a preset's row starts as. Only the first edit uses it. */
  function seedOf(preset: Preset) {
    const built = instantiate(preset, presetLocale);
    return {
      name: t(`marketing.preset.${preset.key}.name`),
      description: t(`marketing.preset.${preset.key}.description`),
      locale: presetLocale,
      subject: built.subject,
      design: built.design,
      bodyHtml: built.bodyHtml,
      bodyText: built.bodyText,
    };
  }

  /** Open the editable version, creating it on the first press. */
  async function edit(preset: Preset, existing: TemplateRow | undefined) {
    if (existing) {
      navigate(`/marketing/templates/${existing.id}`);
      return;
    }
    setBusy(preset.id);
    try {
      const res = await adoptPreset(preset.id, seedOf(preset));
      navigate(`/marketing/templates/${res.template.id}`);
    } catch {
      onError("marketing.error.create");
    } finally {
      setBusy(null);
    }
  }

  /** Throw away the edit. The shipped letter is what is left. */
  async function restore(preset: Preset, existing: TemplateRow) {
    const name = t(`marketing.preset.${preset.key}.name`);
    if (!window.confirm(t("marketing.templates.confirmRestore", { name }))) return;
    setBusy(preset.id);
    try {
      await deleteTemplate(existing.id);
      onChanged();
    } catch {
      onError("marketing.templates.deleteFailed");
    } finally {
      setBusy(null);
    }
  }

  /**
   * Delete a shipped letter from the library.
   *
   * It cannot be deleted the way a saved template is, and the reason is worth
   * knowing: a preset is a constant in the deployed bundle, not a row, so there
   * is nothing to remove - the next deploy would put it straight back. What is
   * stored instead is the operator's decision, on the same row an edit would
   * use, and the letter is then offered nowhere. To everyone using the CRM that
   * IS a delete; the only visible difference is that it can be undone, which is
   * a better property than being unable to.
   *
   * Deleting an edited letter keeps the edit on the row. Bringing it back later
   * therefore returns the shipped wording rather than a half-state where the
   * letter is on the shelf again but the words are gone.
   */
  async function removeFromLibrary(preset: Preset, existing: TemplateRow | undefined) {
    const name = existing?.name ?? t(`marketing.preset.${preset.key}.name`);
    if (!window.confirm(t("marketing.templates.confirmDeletePreset", { name }))) return;
    setBusy(preset.id);
    try {
      const row = existing ?? (await adoptPreset(preset.id, seedOf(preset))).template;
      await patchTemplate(row.id, { hidden: true });
      onChanged();
    } catch {
      onError("marketing.error.save");
    } finally {
      setBusy(null);
    }
  }

  /** A separate template that starts from what this card shows. */
  async function copy(preset: Preset, existing: TemplateRow | undefined) {
    setBusy(preset.id);
    try {
      const seed = seedOf(preset);
      const name = existing?.name ?? seed.name;
      await createTemplate({
        ...seed,
        ...(existing ? { name: existing.name, description: existing.description, subject: existing.subject } : {}),
        // A fresh slug every time: this is a new template, not the edit.
        slug: `${slugify(name) || preset.id}-${Date.now().toString(36).slice(-4)}`,
      });
      onChanged();
    } catch {
      onError("marketing.error.create");
    } finally {
      setBusy(null);
    }
  }

  const inLibrary = PRESETS.filter((p) => !edits.get(p.id)?.hidden);
  const deletedOnes = PRESETS.filter((p) => edits.get(p.id)?.hidden);

  /** One card. The same card whether the letter is in the library or deleted -
   *  only the actions differ, because a deleted letter has one thing to do. */
  function card(preset: Preset, isDeleted: boolean) {
    const edited = edits.get(preset.id);
    const spinning = busy === preset.id;
    return (
      <TemplateCard
        key={preset.id}
        to={!isDeleted && edited ? `/marketing/templates/${edited.id}` : undefined}
        name={edited?.name ?? t(`marketing.preset.${preset.key}.name`)}
        description={edited?.description ?? t(`marketing.preset.${preset.key}.description`)}
        // The card must show what would actually be sent, so an edited preset
        // previews its own design, not the one in the bundle.
        subject={edited?.subject ?? preset.subject[presetLocale]}
        design={edited?.design ?? preset.build(presetLocale)}
        usedBy={isDeleted ? undefined : edited?.usedBy}
        updatedAt={isDeleted ? undefined : edited?.updatedAt}
        tag={
          isDeleted
            ? t("marketing.templates.deletedTag")
            : edited
              ? t("marketing.templates.edited")
              : undefined
        }
        className={isDeleted ? "opacity-60" : undefined}
        actions={
          // Icons only. Four worded buttons wrapped onto three lines in a
          // two-column grid and turned every card into a paragraph of controls;
          // `title` carries the wording for anyone who hovers, `aria-label` for
          // anyone who does not.
          canManage && (
            <>
              {isDeleted ? (
                <IconBtn
                  small
                  disabled={spinning}
                  title={t("marketing.templates.bringBack")}
                  aria-label={t("marketing.templates.bringBack")}
                  onClick={() => void restore(preset, edited!)}
                >
                  {spinning ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
                </IconBtn>
              ) : (
                <>
                  <IconBtn
                    small
                    disabled={spinning}
                    title={t("marketing.templates.edit")}
                    aria-label={t("marketing.templates.edit")}
                    onClick={() => void edit(preset, edited)}
                  >
                    {spinning ? <Loader2 className="size-3.5 animate-spin" /> : <Pencil className="size-3.5" />}
                  </IconBtn>
                  <IconBtn
                    small
                    disabled={spinning}
                    title={t("marketing.templates.saveAsMine")}
                    aria-label={t("marketing.templates.saveAsMine")}
                    onClick={() => void copy(preset, edited)}
                  >
                    <Copy className="size-3.5" />
                  </IconBtn>
                  {edited && (
                    <IconBtn
                      small
                      disabled={spinning}
                      title={t("marketing.templates.restore")}
                      aria-label={t("marketing.templates.restore")}
                      onClick={() => void restore(preset, edited)}
                    >
                      <RotateCcw className="size-3.5" />
                    </IconBtn>
                  )}
                  <IconBtn
                    small
                    disabled={spinning}
                    title={t("marketing.templates.deletePreset")}
                    aria-label={t("marketing.templates.deletePreset")}
                    onClick={() => void removeFromLibrary(preset, edited)}
                  >
                    <Trash2 className="size-3.5" />
                  </IconBtn>
                </>
              )}
            </>
          )
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {inLibrary.map((preset) => card(preset, false))}
      </div>

      {deletedOnes.length > 0 && (
        // One quiet line, not a second grid. A deleted letter that still holds
        // a tile has not been deleted; but a deleted letter with no way back
        // would be worse, and the whole point here is that this one is
        // recoverable, so the line says how many there are and opens them.
        <div className="space-y-3">
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => setShowDeleted((v) => !v)}
          >
            {showDeleted
              ? t("marketing.templates.hideDeleted")
              : t("marketing.templates.showDeleted", { n: deletedOnes.length })}
          </button>
          {showDeleted && (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {deletedOnes.map((preset) => card(preset, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
